import type { fabric } from 'fabric';
import type { FabricEditor } from 'modules/fabric-editor';
import { useCallback, useEffect, useMemo, useState } from 'react';

export type VignetteDirection = 'right' | 'left' | 'up' | 'down';

/**
 * Mirror of the editor core's vignette config (`vignette-plugin.ts`). Defined
 * locally rather than imported so the manager UI stays decoupled from the editor's
 * internal types and the `fabric-editor` public surface stays minimal — the same
 * convention `corner-radii-strategies.ts` uses with `PlaceholderCornerRadii`. The
 * shape is structural, so values flow into `editor.vignette.set` unchanged.
 */
export interface VignetteConfig {
  color: string;
  direction: VignetteDirection;
  /** Where the fade begins, 0..1 along the gradient (0.5 = from the middle). */
  fadeStart: number;
}

const DEFAULT_DIRECTION: VignetteDirection = 'right';
const DEFAULT_FADE_START = 0.5;
const DEFAULT_COLOR = '#cccccc';

interface Aggregate<T> {
  value: T | undefined;
  isMixed: boolean;
}

export interface VignetteControl {
  enabled: Aggregate<boolean>;
  color: Aggregate<string>;
  direction: Aggregate<VignetteDirection>;
  fadeStart: Aggregate<number>;
  setEnabled: (on: boolean) => void;
  setColor: (color: string) => void;
  setDirection: (direction: VignetteDirection) => void;
  setFadeStart: (fadeStart: number) => void;
}

const configOf = (o: fabric.Object): VignetteConfig | undefined => (o.data as { vignette?: VignetteConfig } | undefined)?.vignette;

/** Existing config, or a default one seeded from the object's current solid fill. */
const seedOf = (o: fabric.Object): VignetteConfig =>
  configOf(o) ?? {
    color: typeof o.fill === 'string' ? o.fill : DEFAULT_COLOR,
    direction: DEFAULT_DIRECTION,
    fadeStart: DEFAULT_FADE_START,
  };

function aggregate<T>(objects: fabric.Object[], read: (o: fabric.Object) => T | undefined): Aggregate<T> {
  if (objects.length === 0) return { value: undefined, isMixed: false };
  const first = read(objects[0]);
  for (let i = 1; i < objects.length; i++) {
    if (read(objects[i]) !== first) return { value: undefined, isMixed: true };
  }
  return { value: first, isMixed: false };
}

/**
 * Reads/writes the vignette effect across one or more selected objects. Each field
 * is aggregated independently (so a multi-selection that agrees on, say, direction
 * but differs on color reports only color as mixed). Writes route through
 * `editor.vignette.set`, which rebuilds the gradient `fill` from the config and
 * records one undo step per call; the hook re-reads on `object:modified` so an undo
 * or an external change keeps the inspector in sync.
 */
export function useVignette(objects: fabric.Object[], editor: FabricEditor): VignetteControl {
  const objectSet = useMemo(() => new Set(objects), [objects]);

  const read = useCallback(
    (): Omit<VignetteControl, 'setEnabled' | 'setColor' | 'setDirection' | 'setFadeStart'> => ({
      enabled: aggregate(objects, (o) => Boolean(configOf(o))),
      color: aggregate(objects, (o) => configOf(o)?.color),
      direction: aggregate(objects, (o) => configOf(o)?.direction),
      fadeStart: aggregate(objects, (o) => configOf(o)?.fadeStart),
    }),
    [objects],
  );

  const [state, setState] = useState(read);

  useEffect(() => {
    setState(read());
    const off = editor.on('object:modified', (e) => {
      if (objectSet.has(e.target as fabric.Object)) setState(read());
    });
    return () => off();
  }, [editor, objectSet, read]);

  const setEnabled = useCallback(
    (on: boolean) => {
      for (const o of objects) {
        if (on === Boolean(configOf(o))) continue; // already in target state
        editor.vignette?.set(o, on ? seedOf(o) : undefined);
      }
      setState(read());
    },
    [objects, editor, read],
  );

  const patch = useCallback(
    (changes: Partial<VignetteConfig>) => {
      for (const o of objects) {
        if (!configOf(o)) continue; // only meaningful while the vignette is on
        editor.vignette?.set(o, { ...seedOf(o), ...changes });
      }
      setState(read());
    },
    [objects, editor, read],
  );

  const setColor = useCallback((color: string) => patch({ color }), [patch]);
  const setDirection = useCallback((direction: VignetteDirection) => patch({ direction }), [patch]);
  const setFadeStart = useCallback((fadeStart: number) => patch({ fadeStart }), [patch]);

  return { ...state, setEnabled, setColor, setDirection, setFadeStart };
}
